/**
 * Prioritization Contexts Store
 *
 * Manages PrioritizationContext records — the first-class context objects that
 * implement the Context Invariant (P01).
 *
 * Context creation goes through the create_prioritization_context RPC (never a
 * direct client insert) — authority transitions must be transactional.
 *
 * See 10C-schema-design-configurations-and-context.md.
 */

import { create } from 'zustand';
import { getSafeSupabaseClient } from '../../lib/supabaseSync';
import type {
  PrioritizationContext,
  CreateContextParams,
  ContextReadinessResult,
} from '../../types/prioritization/context';

interface ContextsState {
  contexts: Record<string, PrioritizationContext>;
  isLoading: boolean;
  error: string | null;

  // Actions
  createContext: (params: CreateContextParams) => Promise<PrioritizationContext | null>;
  fetchContextForCycle: (cycleId: string) => Promise<PrioritizationContext | null>;
  fetchContextForSession: (sessionId: string) => Promise<PrioritizationContext | null>;
  fetchContextById: (contextId: string) => Promise<PrioritizationContext | null>;
  fetchMostRecentActiveContext: (workspaceId: string) => Promise<PrioritizationContext | null>;
  evaluateContextReadiness: (contextId: string) => Promise<ContextReadinessResult | null>;
  getContextById: (contextId: string) => PrioritizationContext | null;
  clearError: () => void;
}

export const useContextsStore = create<ContextsState>((set, get) => ({
  contexts: {},
  isLoading: false,
  error: null,

  createContext: async (params: CreateContextParams) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) {
      set({ error: 'Supabase client not available' });
      return null;
    }

    set({ isLoading: true, error: null });

    try {
      // Authority action: must go through RPC, not direct insert
      const { data, error } = await supabase.rpc('create_prioritization_context', {
        p_workspace_id:     params.workspace_id,
        p_context_type:     params.context_type,
        p_cycle_id:         params.cycle_id         ?? null,
        p_group_id:         params.group_id         ?? null,
        p_owner_id:         params.owner_id         ?? null,
        p_configuration_id: params.configuration_id ?? null,
        p_name:             params.name             ?? null,
        p_actor_id:         params.actor_id         ?? null,
      });

      if (error) {
        set({ isLoading: false, error: error.message });
        return null;
      }

      const contextId = data as string;

      // Fetch the full context record
      const context = await get().fetchContextById(contextId);
      set({ isLoading: false });
      return context;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error creating context';
      set({ isLoading: false, error: message });
      return null;
    }
  },

  fetchContextById: async (contextId: string) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return null;

    try {
      const { data, error } = await supabase
        .from('prioritization_contexts')
        .select('*')
        .eq('id', contextId)
        .single();

      if (error || !data) return null;

      const context = data as PrioritizationContext;
      set((state) => ({
        contexts: { ...state.contexts, [contextId]: context },
      }));
      return context;
    } catch {
      return null;
    }
  },

  fetchContextForCycle: async (cycleId: string) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return null;

    // Check cache first
    const cached = Object.values(get().contexts).find(
      (c) => c.cycle_id === cycleId && c.context_type === 'cycle'
    );
    if (cached) return cached;

    try {
      const { data, error } = await supabase
        .from('prioritization_contexts')
        .select('*')
        .eq('cycle_id', cycleId)
        .eq('context_type', 'cycle')
        .single();

      if (error || !data) return null;

      const context = data as PrioritizationContext;
      set((state) => ({
        contexts: { ...state.contexts, [context.id]: context },
      }));
      return context;
    } catch {
      return null;
    }
  },

  fetchContextForSession: async (sessionId: string) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return null;

    try {
      const { data, error } = await supabase
        .from('prioritization_sessions')
        .select('context_id')
        .eq('id', sessionId)
        .single();

      if (error || !data?.context_id) return null;

      return get().fetchContextById(data.context_id);
    } catch {
      return null;
    }
  },

  fetchMostRecentActiveContext: async (workspaceId: string) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return null;

    // Check cache first — return the most recently created active context for this workspace
    const cached = Object.values(get().contexts)
      .filter((c) => c.workspace_id === workspaceId && c.status === 'active')
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
    if (cached) return cached;

    try {
      const { data, error } = await supabase
        .from('prioritization_contexts')
        .select('*')
        .eq('workspace_id', workspaceId)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error || !data) return null;

      const context = data as PrioritizationContext;
      set((state) => ({
        contexts: { ...state.contexts, [context.id]: context },
      }));
      return context;
    } catch {
      return null;
    }
  },

  evaluateContextReadiness: async (contextId: string) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return null;

    try {
      const { data, error } = await supabase.rpc('evaluate_context_readiness', {
        p_context_id: contextId,
      });

      if (error) {
        console.error('[ContextsStore] evaluateContextReadiness error:', error);
        return null;
      }

      return data as ContextReadinessResult;
    } catch (err) {
      console.error('[ContextsStore] evaluateContextReadiness exception:', err);
      return null;
    }
  },

  getContextById: (contextId: string) => {
    return get().contexts[contextId] ?? null;
  },

  clearError: () => set({ error: null }),
}));
